import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { ProductCombobox } from "./ProductCombobox";
import { DepotField, SimpleSelect } from "./DepotField";
import { useDepot } from "../context/DepotContext";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, money, qtyFmt, daysAheadStr, PAYMENT_METHODS } from "../lib/api";

export function MovementForm({ type, cfg, products, onDone }) {
  const { user, isAdmin } = useAuth();
  const { depotParam, depots } = useDepot();
  const [depotId, setDepotId] = useState(depotParam || "");
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");
  const [party, setParty] = useState("");
  const [payment, setPayment] = useState("Cash");
  const [note, setNote] = useState("");
  const [dueDate, setDueDate] = useState(daysAheadStr(30));
  const [busy, setBusy] = useState(false);
  const isIn = type === "stock_in";

  useEffect(() => {
    if (!isAdmin) setDepotId(user.depot_id);
    else if (depotParam) setDepotId(depotParam);
    else if (!depotId && depots.length) setDepotId(depots[0].id);
  }, [isAdmin, user, depotParam, depots, depotId]);

  const pick = (p) => { setProduct(p); setPrice(String(p[cfg.priceKey] ?? "")); };
  const available = product ? product.stock?.[depotId] ?? 0 : null;
  const total = (Number(qty) || 0) * (Number(price) || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!product || !depotId) { toast.error("Select a product and depot"); return; }
    setBusy(true);
    const common = { product_id: product.id, depot_id: depotId, qty: Number(qty), payment_method: payment, note };
    const body = isIn
      ? { ...common, unit_cost: Number(price), supplier: party, ...(payment === "Credit" ? { due_date: dueDate } : {}) }
      : { ...common, unit_price: Number(price), customer: party };
    try {
      await api.post(isIn ? "/stock-in" : "/stock-out", body);
      toast.success(isIn ? `Received ${qty} ${product.unit} of ${product.name}` : `Sale recorded: ${money(total)}`);
      setProduct(null); setQty(""); setPrice(""); setParty(""); setNote("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 p-5" data-testid={`${cfg.testid}-form`}>
      <DepotField value={depotId} onChange={setDepotId} testid={`${cfg.testid}-depot-select`} />
      <div className="space-y-1.5">
        <Label className="label-mono">Product</Label>
        <ProductCombobox products={products} value={product} onChange={pick} depotId={depotId} testid={`${cfg.testid}-product`} />
        {product && (
          <div className="text-xs text-slate-400" data-testid={`${cfg.testid}-available`}>
            Available in depot: <span className={`font-mono ${available <= product.reorder_level ? "text-amber-400" : "text-slate-200"}`}>{qtyFmt(available)} {product.unit}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="label-mono">Quantity</Label>
          <Input type="number" step="any" min="0" required value={qty} onChange={(e) => setQty(e.target.value)} className="bg-slate-950" data-testid={`${cfg.testid}-qty-input`} />
        </div>
        <div className="space-y-1.5">
          <Label className="label-mono">{cfg.priceLabel} (TSh)</Label>
          <Input type="number" step="0.01" min="0" required value={price} onChange={(e) => setPrice(e.target.value)} className="bg-slate-950" data-testid={`${cfg.testid}-price-input`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="label-mono">{cfg.party}{payment === "Credit" ? " (required for credit)" : ""}</Label>
          <Input value={party} onChange={(e) => setParty(e.target.value)} required={payment === "Credit"} placeholder={payment === "Credit" ? `${cfg.party} name` : "Optional"} className="bg-slate-950" data-testid={`${cfg.testid}-party-input`} />
        </div>
        <SimpleSelect label="Payment" value={payment} onChange={setPayment} options={PAYMENT_METHODS} testid={`${cfg.testid}-payment-select`} />
      </div>
      {isIn && payment === "Credit" && (
        <div className="space-y-1.5">
          <Label className="label-mono">Pay supplier by (due date)</Label>
          <Input type="date" required value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="bg-slate-950" data-testid="stock-in-due-date-input" />
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="label-mono">Note</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className="bg-slate-950" data-testid={`${cfg.testid}-note-input`} />
      </div>
      <div className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950 px-4 py-3">
        <span className="label-mono">Total</span>
        <span className={`font-mono text-xl font-bold ${isIn ? "text-rose-400" : "text-emerald-400"}`} data-testid={`${cfg.testid}-total`}>{money(total)}</span>
      </div>
      <Button type="submit" disabled={busy} className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid={`${cfg.testid}-submit-button`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {cfg.submit}
      </Button>
    </form>
  );
}
